import React, { useCallback, useEffect, useState } from "react";
import useApi from "@/hooks/useApi";
import useToastAlert from "@/hooks/useToastAlert";
import { Severity } from "@velaro/velaro-component-lib";
type BaseStatusType = "Available" | "Away" | "Offline";

interface CustomStatusEntry {
  id: number;
  siteId: number;
  name: string;
  baseStatus: BaseStatusType;
  color?: string;
  sortOrder: number;
  isActive: boolean;
  createdBy: string;
  createdAt: string;
}

interface ListResponse {
  entries: CustomStatusEntry[];
  activeCount: number;
  maxCustomStatuses: number;
  enabled: boolean;
}

const BASE_STATUS_OPTIONS: { value: BaseStatusType; label: string; color: string }[] = [
  { value: "Available", label: "Available", color: "bg-success-green-500" },
  { value: "Away", label: "Away", color: "bg-sweet-orange-500" },
  { value: "Offline", label: "Offline", color: "bg-slate-400" },
];

const DEFAULT_COLORS = [
  "#22c55e", "#3b82f6", "#f59e0b", "#ef4444", "#8b5cf6",
  "#ec4899", "#06b6d4", "#84cc16", "#f97316", "#6366f1",
];

export default function CustomStatusPanel() {
  const api = useApi();
  const { displayToast } = useToastAlert();
  const [entries, setEntries] = useState<CustomStatusEntry[]>([]);
  const [activeCount, setActiveCount] = useState(0);
  const [maxCustomStatuses, setMaxCustomStatuses] = useState(10);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingEntry, setEditingEntry] = useState<CustomStatusEntry | null>(null);

  // Form state
  const [formName, setFormName] = useState("");
  const [formBaseStatus, setFormBaseStatus] = useState<BaseStatusType>("Away");
  const [formColor, setFormColor] = useState("#3b82f6");
  const [formSortOrder, setFormSortOrder] = useState(0);

  const fetchData = useCallback(async () => {
    try {
      const resp = await api.messaging.get("CustomStatus");
      const data: ListResponse = await resp.json();
      if (Array.isArray(data?.entries)) {
        setEntries(data.entries);
        setActiveCount(data.activeCount);
        setMaxCustomStatuses(data.maxCustomStatuses);
      }
    } catch {
      // Safe default
    } finally {
      setLoading(false);
    }
  }, [api.messaging]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  function openCreate() {
    setEditingEntry(null);
    setFormName("");
    setFormBaseStatus("Away");
    setFormColor(DEFAULT_COLORS[entries.length % DEFAULT_COLORS.length]);
    setFormSortOrder(entries.length);
    setShowModal(true);
  }

  function openEdit(entry: CustomStatusEntry) {
    setEditingEntry(entry);
    setFormName(entry.name);
    setFormBaseStatus(entry.baseStatus);
    setFormColor(entry.color || "#3b82f6");
    setFormSortOrder(entry.sortOrder);
    setShowModal(true);
  }

  async function handleSave() {
    if (!formName.trim()) {
      displayToast(Severity.Error, "Status name is required.");
      return;
    }

    try {
      if (editingEntry) {
        const resp = await api.messaging.put(`CustomStatus/${editingEntry.id}`, {
          name: formName.trim(),
          baseStatus: formBaseStatus,
          color: formColor,
          sortOrder: formSortOrder,
        });
        if (!resp.ok) {
          const err = await resp.json();
          displayToast(Severity.Error, err?.message || "Failed to update.");
          return;
        }
        displayToast(Severity.Success, `Updated "${formName.trim()}".`);
      } else {
        const resp = await api.messaging.post("CustomStatus", {
          name: formName.trim(),
          baseStatus: formBaseStatus,
          color: formColor,
          sortOrder: formSortOrder,
        });
        if (!resp.ok) {
          const err = await resp.json();
          displayToast(Severity.Error, err?.message || "Failed to create.");
          return;
        }
        displayToast(Severity.Success, `Created "${formName.trim()}".`);
      }
      setShowModal(false);
      fetchData();
    } catch {
      displayToast(Severity.Error, "An error occurred.");
    }
  }

  async function handleToggleActive(entry: CustomStatusEntry) {
    try {
      const resp = await api.messaging.put(`CustomStatus/${entry.id}`, {
        isActive: !entry.isActive,
      });
      if (!resp.ok) {
        const err = await resp.json();
        displayToast(Severity.Error, err?.message || "Failed to toggle.");
        return;
      }
      displayToast(Severity.Success, `${entry.name} ${entry.isActive ? "deactivated" : "activated"}.`);
      fetchData();
    } catch {
      displayToast(Severity.Error, "An error occurred.");
    }
  }

  async function handleDelete(entry: CustomStatusEntry) {
    if (!window.confirm(`Delete "${entry.name}"? Agents using this status will be reset to "${entry.baseStatus}".`)) {
      return;
    }
    try {
      const resp = await api.messaging.delete(`CustomStatus/${entry.id}`);
      if (!resp.ok) {
        const err = await resp.json();
        displayToast(Severity.Error, err?.message || "Failed to delete.");
        return;
      }
      displayToast(Severity.Success, `Deleted "${entry.name}".`);
      fetchData();
    } catch {
      displayToast(Severity.Error, "An error occurred.");
    }
  }

  const baseStatusBadge = (bs: string) => {
    const cfg = BASE_STATUS_OPTIONS.find(o => o.value === bs);
    return (
      <span className={`inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full text-xs font-medium ${
        bs === "Available" ? "bg-success-green-50 text-success-green-700" :
        bs === "Away" ? "bg-sweet-orange-50 text-sweet-orange-700" :
        "bg-slate-100 text-slate-600"
      }`}>
        <span className={`w-2 h-2 rounded-full ${cfg?.color ?? "bg-slate-400"}`}></span>
        {bs}
      </span>
    );
  };

  if (loading) {
    return <div className="p-6 text-sm text-slate-500">Loading custom statuses...</div>;
  }

  return (
    <div className="p-6">
      <div className="flex items-center justify-between mb-4">
        <div>
          <h2 className="text-lg font-semibold">Custom Agent Statuses</h2>
          <p className="text-sm text-slate-500">
            Define custom statuses that map to base statuses for routing and reporting.
          </p>
        </div>
        <div className="flex items-center gap-3">
          <span className="text-sm text-slate-500">{activeCount} / {maxCustomStatuses} active</span>
          <button
            className="px-3 py-1.5 text-sm font-medium text-white bg-primary rounded hover:bg-primary disabled:opacity-50 disabled:cursor-not-allowed"
            onClick={openCreate}
            disabled={activeCount >= maxCustomStatuses}
          >
            + Add Status
          </button>
        </div>
      </div>

      {entries.length === 0 ? (
        <div className="text-center py-12 text-slate-400">
          <p className="text-sm">No custom statuses configured.</p>
          <p className="text-xs mt-1">Click &quot;+ Add Status&quot; to create one.</p>
        </div>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-left">
            <thead>
              <tr className="border-b border-slate-200 text-xs text-slate-500 uppercase tracking-wide">
                <th className="px-3 py-2">Color</th>
                <th className="px-3 py-2">Name</th>
                <th className="px-3 py-2">Base Status</th>
                <th className="px-3 py-2">Sort</th>
                <th className="px-3 py-2">Active</th>
                <th className="px-3 py-2">Actions</th>
              </tr>
            </thead>
            <tbody>
              {entries.map((entry) => (
                <tr key={entry.id} className={`border-b border-slate-100 text-sm ${!entry.isActive ? "opacity-50" : ""}`}>
                  <td className="px-3 py-1.5">
                    <span
                      className="inline-block w-5 h-5 rounded-full border border-slate-200"
                      style={{ backgroundColor: entry.color || "#94a3b8" }}
                    ></span>
                  </td>
                  <td className="px-3 py-1.5 font-medium">{entry.name}</td>
                  <td className="px-3 py-1.5">{baseStatusBadge(entry.baseStatus)}</td>
                  <td className="px-3 py-1.5 text-slate-500">{entry.sortOrder}</td>
                  <td className="px-3 py-1.5">
                    <button
                      onClick={() => handleToggleActive(entry)}
                      className={`px-2 py-0.5 text-xs rounded ${
                        entry.isActive
                          ? "bg-success-green-50 text-success-green-700"
                          : "bg-slate-100 text-slate-500"
                      }`}
                      aria-label={`${entry.isActive ? "Deactivate" : "Activate"} ${entry.name}`}
                    >
                      {entry.isActive ? "Active" : "Inactive"}
                    </button>
                  </td>
                  <td className="px-3 py-1.5">
                    <div className="flex items-center gap-2">
                      <button
                        onClick={() => openEdit(entry)}
                        className="text-xs text-primary hover:underline"
                        aria-label={`Edit ${entry.name}`}
                      >
                        Edit
                      </button>
                      <button
                        onClick={() => handleDelete(entry)}
                        className="text-xs text-crimson-red-500 hover:underline"
                        aria-label={`Delete ${entry.name}`}
                      >
                        Delete
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Add/Edit Modal */}
      {showModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
          <div className="bg-white dark:bg-stone-500 rounded-lg shadow-lg w-96 p-6" role="dialog" aria-modal="true">
            <h3 className="text-lg font-semibold mb-4">
              {editingEntry ? "Edit Custom Status" : "Add Custom Status"}
            </h3>
            <div className="space-y-3">
              <div>
                <label htmlFor="cs-name" className="block text-sm font-medium text-slate-700 dark:text-white mb-1">Name</label>
                <input
                  id="cs-name"
                  type="text"
                  maxLength={50}
                  className="w-full px-3 py-2 border rounded text-sm dark:bg-stone-400 dark:border-stone-200 dark:text-white focus-visible:outline-2 focus-visible:outline-primary"
                  value={formName}
                  onChange={(e) => setFormName(e.target.value)}
                  placeholder="e.g., Lunch, Training, Break"
                />
              </div>
              <div>
                <label htmlFor="cs-base" className="block text-sm font-medium text-slate-700 dark:text-white mb-1">Base Status</label>
                <select
                  id="cs-base"
                  className="w-full px-3 py-2 border rounded text-sm dark:bg-stone-400 dark:border-stone-200 dark:text-white focus-visible:outline-2 focus-visible:outline-primary"
                  value={formBaseStatus}
                  onChange={(e) => setFormBaseStatus(e.target.value as BaseStatusType)}
                >
                  {BASE_STATUS_OPTIONS.map((o) => (
                    <option key={o.value} value={o.value}>{o.label}</option>
                  ))}
                </select>
                <p className="text-xs text-slate-400 mt-1">Determines routing behavior and report grouping.</p>
              </div>
              <div>
                <label htmlFor="cs-color" className="block text-sm font-medium text-slate-700 dark:text-white mb-1">Color</label>
                <div className="flex items-center gap-2">
                  <input
                    id="cs-color"
                    type="color"
                    className="w-10 h-8 rounded border cursor-pointer"
                    value={formColor}
                    onChange={(e) => setFormColor(e.target.value)}
                  />
                  <span className="text-xs text-slate-400">{formColor}</span>
                </div>
              </div>
              <div>
                <label htmlFor="cs-sort" className="block text-sm font-medium text-slate-700 dark:text-white mb-1">Sort Order</label>
                <input
                  id="cs-sort"
                  type="number"
                  min={0}
                  className="w-20 px-3 py-2 border rounded text-sm dark:bg-stone-400 dark:border-stone-200 dark:text-white focus-visible:outline-2 focus-visible:outline-primary"
                  value={formSortOrder}
                  onChange={(e) => setFormSortOrder(parseInt(e.target.value) || 0)}
                />
              </div>
            </div>
            <div className="flex justify-end gap-2 mt-5">
              <button
                onClick={() => setShowModal(false)}
                className="px-4 py-2 text-sm border rounded hover:bg-slate-50 dark:hover:bg-stone-400"
              >
                Cancel
              </button>
              <button
                onClick={handleSave}
                className="px-4 py-2 text-sm font-medium text-white bg-primary rounded hover:bg-primary"
              >
                {editingEntry ? "Save Changes" : "Create"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
